import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Gear,
  Envelope,
  Receipt,
  Check,
  Warning,
  PaperPlaneTilt,
  Bell
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user, isAdmin } = useAuth();
  const [cfdiStatus, setCfdiStatus] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [message, setMessage] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [maintenanceResult, setMaintenanceResult] = useState(null);

  const runCleanupCancelled = async () => {
    if (!window.confirm('¿Ejecutar limpieza de cuotas de contratos cancelados? Es seguro re-ejecutar.')) return;
    try {
      setCleaning(true);
      setMaintenanceResult(null);
      const { data } = await axios.post(`${API}/api/dashboard/cleanup-cancelled-schedules`, {}, { withCredentials: true });
      setMaintenanceResult({ operation: 'cleanup-cancelled-schedules', ...data });
    } catch (err) {
      setMaintenanceResult({ error: err?.response?.data?.detail || err?.message });
    } finally {
      setCleaning(false);
    }
  };

  const runMigrateLegacy = async () => {
    if (!window.confirm('¿Ejecutar migración de archivos legacy a Object Storage? Es idempotente.')) return;
    try {
      setMigrating(true);
      setMaintenanceResult(null);
      const { data } = await axios.post(`${API}/api/documents/migrate-legacy`, {}, { withCredentials: true });
      setMaintenanceResult({ operation: 'migrate-legacy', ...data });
    } catch (err) {
      setMaintenanceResult({ error: err?.response?.data?.detail || err?.message });
    } finally {
      setMigrating(false);
    }
  };

  useEffect(() => {
    checkCfdiStatus();
  }, []);

  const checkCfdiStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/cfdi/status`, { withCredentials: true });
      setCfdiStatus(data);
    } catch (error) {
      // CFDI status check is non-critical; user can still send test emails.
      console.warn('[Settings] CFDI status check failed:', error?.message || error);
    }
  }, []);

  const sendTestEmail = async () => {
    if (!testEmail) return;
    setSending(true);
    setMessage(null);
    
    try {
      const { data } = await axios.post(
        `${API}/api/notifications/send-test?recipient_email=${encodeURIComponent(testEmail)}`,
        {},
        { withCredentials: true }
      );
      setMessage({ type: 'success', text: data.message });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error al enviar correo de prueba' 
      });
    } finally {
      setSending(false);
    }
  };

  const sendOverdueAlerts = async () => {
    setSendingAlerts(true);
    setMessage(null);
    
    try {
      const { data } = await axios.post(
        `${API}/api/notifications/send-overdue-alerts`,
        {},
        { withCredentials: true }
      );
      setMessage({ 
        type: 'success', 
        text: `${data.message}. Pagos vencidos: ${data.overdue_count}, Monto total: $${data.total_amount?.toLocaleString()}` 
      });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error al enviar alertas' 
      });
    } finally {
      setSendingAlerts(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <Warning size={48} className="mx-auto text-yellow-500 mb-4" />
        <h2 className="text-xl font-bold">Acceso Restringido</h2>
        <p className="text-gray-500">Solo los administradores pueden acceder a esta sección.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Configuraciones</h1>
        <p className="text-[#64748B]">Configuración de integraciones y notificaciones</p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Notifications */}
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Envelope size={24} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-[#0F172A]">Notificaciones por Email</h2>
              <p className="text-sm text-[#64748B]">Configuración de Gmail</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-2">Variables de Entorno Requeridas:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><code className="bg-gray-200 px-1 rounded">GMAIL_USER</code> - Tu correo de Gmail</li>
                <li><code className="bg-gray-200 px-1 rounded">GMAIL_APP_PASSWORD</code> - Contraseña de aplicación</li>
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                Genera una contraseña de aplicación en: Google Account → Seguridad → Contraseñas de aplicaciones
              </p>
            </div>

            <div>
              <label className="label">Enviar Correo de Prueba</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className="input flex-1"
                  data-testid="test-email-input"
                />
                <button
                  onClick={sendTestEmail}
                  disabled={sending || !testEmail}
                  className="btn-primary flex items-center gap-2"
                  data-testid="send-test-email-btn"
                >
                  {sending ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <PaperPlaneTilt size={18} />
                  )}
                  Enviar
                </button>
              </div>
            </div>

            <div className="pt-4 border-t">
              <button
                onClick={sendOverdueAlerts}
                disabled={sendingAlerts}
                className="w-full btn-secondary flex items-center justify-center gap-2 bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
                data-testid="send-overdue-alerts-btn"
              >
                {sendingAlerts ? (
                  <div className="w-4 h-4 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
                ) : (
                  <Bell size={18} />
                )}
                Enviar Alerta de Pagos Vencidos
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Envía un resumen de pagos vencidos al correo del administrador
              </p>
            </div>
          </div>
        </div>

        {/* Facturama CFDI */}
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Receipt size={24} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-[#0F172A]">Facturación CFDI</h2>
              <p className="text-sm text-[#64748B]">Integración con Facturama</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Status */}
            <div className={`p-4 rounded-lg flex items-center gap-3 ${
              cfdiStatus?.configured 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-yellow-50 border border-yellow-200'
            }`}>
              {cfdiStatus?.configured ? (
                <Check size={24} className="text-green-600" />
              ) : (
                <Warning size={24} className="text-yellow-600" />
              )}
              <div>
                <p className={`font-medium ${cfdiStatus?.configured ? 'text-green-800' : 'text-yellow-800'}`}>
                  {cfdiStatus?.configured ? 'Configurado' : 'No Configurado'}
                </p>
                <p className="text-sm text-gray-600">{cfdiStatus?.message}</p>
                {cfdiStatus?.sandbox_mode && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs rounded">
                    Modo Sandbox
                  </span>
                )}
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-2">Variables de Entorno Requeridas:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><code className="bg-gray-200 px-1 rounded">FACTURAMA_USER</code> - Usuario de Facturama</li>
                <li><code className="bg-gray-200 px-1 rounded">FACTURAMA_PASSWORD</code> - Contraseña</li>
                <li><code className="bg-gray-200 px-1 rounded">FACTURAMA_SANDBOX</code> - true/false</li>
                <li><code className="bg-gray-200 px-1 rounded">FACTURAMA_ZIP_CODE</code> - CP de expedición</li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-medium text-blue-800 mb-2">Pasos para Configurar:</h3>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Crear cuenta en <a href="https://facturama.mx" target="_blank" rel="noopener noreferrer" className="underline">facturama.mx</a></li>
                <li>Subir tu FIEL/CSD (certificados SAT)</li>
                <li>Obtener credenciales de API</li>
                <li>Configurar variables de entorno</li>
                <li>Probar en modo Sandbox</li>
              </ol>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm text-gray-500">
                <strong>Funcionalidades disponibles:</strong> Generación de CFDI 4.0, descarga de XML/PDF, 
                cancelación de facturas. Los CFDI se generan desde la sección de Pagos.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mantenimiento (Admin only) */}
      {isAdmin && (
        <div className="card p-6">
          <h2 className="text-lg font-heading font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Gear size={20} className="text-slate-600" />
            Mantenimiento
          </h2>
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-[#0F172A] text-sm">Limpiar cuotas de contratos cancelados</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Marca como <b>cancelado</b> cualquier cuota (schedule) pendiente/atrasada cuyo contrato ya esté cancelado o reestructurado.
                  Elimina Conductores Desconocidos del modal de Cartera Vencida. Idempotente y seguro.
                </p>
              </div>
              <button
                onClick={runCleanupCancelled}
                disabled={cleaning}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white text-sm font-semibold rounded-lg flex-shrink-0"
                data-testid="cleanup-cancelled-btn"
              >
                {cleaning ? 'Ejecutando…' : 'Ejecutar limpieza'}
              </button>
            </div>
            <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-[#0F172A] text-sm">Migrar archivos legacy a Object Storage</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Sube al almacenamiento persistente cualquier PDF que aún esté en el disco del contenedor.
                </p>
              </div>
              <button
                onClick={runMigrateLegacy}
                disabled={migrating}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white text-sm font-semibold rounded-lg flex-shrink-0"
                data-testid="migrate-legacy-btn"
              >
                {migrating ? 'Migrando…' : 'Migrar archivos'}
              </button>
            </div>
            {maintenanceResult && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800" data-testid="maintenance-result">
                <pre className="whitespace-pre-wrap break-words font-mono">{JSON.stringify(maintenanceResult, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Additional Info */}
      <div className="card p-6">
        <h2 className="text-lg font-heading font-bold text-[#0F172A] mb-4">Información del Sistema</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Usuario</p>
            <p className="font-medium">{user?.name}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Rol</p>
            <p className="font-medium capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Email</p>
            <p className="font-medium">{user?.email}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Versión</p>
            <p className="font-medium">LUCARS v1.0</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
